import { ApolloError } from "@apollo/client";
import { OperatorManyUseCaseEnum } from "gql/generated";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useOperatorsByIdsEventCreationLazyQuery,
  useOperatorsManyEventCreationLazyQuery,
} from "gql/generated";
import { LocationListData } from "apps/events/types/LocationListData";
import { mapOperatorsToLocationListData } from "./mapOperatorsToLocationListData";

type UseEventOperatorAndOrganizationListProps = {
  isOpen: boolean;
  isOrganization: boolean;
  isOperator: boolean;
  defaultOrganizationId: string;
  defaultOperatorEntityIds: string[];
  defaultOrganizationIdsForOperatorUser: string[];
  /** Prefetch the event organizer listing when editing (org/public search mode). */
  organizerEntityIdToPrefetch?: string;
  /** When set, preload this operator even for non-operator users (e.g. org admin on a listing page). */
  limitHostToOperatorEntityId?: string;
  /**
   * Storefront connected listings offered as event organizers. Loaded by id
   * so the lookup is not scoped to the storefront id as if it were an org.
   */
  organizerEntityIds?: string[];
  /** Pre-built location data for the host operator, injected directly to avoid query auth issues. */
  preloadedOrganizerLocation?: LocationListData;
};

const SEARCH_RESULT_LIMIT = 25;

export const useEventOperatorAndOrganizationList = ({
  isOpen,
  isOrganization,
  isOperator,
  defaultOrganizationId,
  defaultOperatorEntityIds,
  defaultOrganizationIdsForOperatorUser,
  organizerEntityIdToPrefetch,
  limitHostToOperatorEntityId,
  organizerEntityIds,
  preloadedOrganizerLocation,
}: UseEventOperatorAndOrganizationListProps) => {
  const [allLocations, setAllLocations] = useState<LocationListData[]>([]);
  const [searchResults, setSearchResults] = useState<LocationListData[]>([]);
  const [isSearchingOperators, setIsSearchingOperators] = useState(false);
  const searchRequestIdRef = useRef(0);

  /**
   * Preload state is tracked separately from the lazy query's own loading/error
   * flags because every caller here shares one query instance. The consumer
   * turns `loading` into a full-screen spinner that replaces the modal body, so
   * letting a venue search flip it would unmount the step mid-keystroke and
   * throw away the open venue picker.
   */
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadError, setPreloadError] = useState<ApolloError | undefined>();

  const [getOperators] = useOperatorsManyEventCreationLazyQuery();
  const [getOperatorsByIds] = useOperatorsByIdsEventCreationLazyQuery();
  const organizerEntityIdsKey = organizerEntityIds?.join(",") ?? "";

  const runPreload = useCallback(
    (request: Promise<{ error?: ApolloError }>) => {
      setIsPreloading(true);
      setPreloadError(undefined);
      request
        .then(result => setPreloadError(result?.error))
        .catch((error: ApolloError) => setPreloadError(error))
        .finally(() => setIsPreloading(false));
    },
    [],
  );

  const getOrganizationsFilter = useCallback(() => {
    return isOrganization || (!isOrganization && !isOperator)
      ? [defaultOrganizationId]
      : defaultOrganizationIdsForOperatorUser;
  }, [
    defaultOrganizationId,
    defaultOrganizationIdsForOperatorUser,
    isOperator,
    isOrganization,
  ]);

  const getUseCase = useCallback(() => {
    return isOrganization || isOperator
      ? OperatorManyUseCaseEnum.Internal
      : OperatorManyUseCaseEnum.External;
  }, [isOrganization, isOperator]);

  const mergeIntoAllLocations = useCallback((locations: LocationListData[]) => {
    setAllLocations(prev => {
      const byValue = new Map(prev.map(location => [location.value, location]));
      locations.forEach(location => byValue.set(location.value, location));
      return Array.from(byValue.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    });
  }, []);

  const fetchOperators = useCallback(
    (
      filter: Record<string, unknown>,
      limit: number,
      onResult: (locations: LocationListData[]) => void,
    ) => {
      return getOperators({
        variables: {
          useCase: getUseCase(),
          includeInvitedOrAcceptedStaffCounts: false,
          filter: {
            organizations: getOrganizationsFilter(),
            ...filter,
          },
          limit,
        },
        onCompleted: data => {
          if (data.operatorMany) {
            onResult(mapOperatorsToLocationListData(data.operatorMany));
          }
        },
      });
    },
    [getOperators, getOrganizationsFilter, getUseCase],
  );

  const preferOrganization = useCallback(
    (locations: LocationListData[]) => {
      if (!defaultOrganizationId) return locations;
      return locations.map(location => ({
        ...location,
        data: {
          ...location.data,
          enrollments: [...location.data.enrollments].sort((a, b) => {
            if (a.organizationId === defaultOrganizationId) return -1;
            if (b.organizationId === defaultOrganizationId) return 1;
            return 0;
          }),
        },
      }));
    },
    [defaultOrganizationId],
  );

  /**
   * Operator users: preload their own listings (~5–10).
   * Storefronts: preload connected listings by id (operatorsByIds), because
   * operatorMany would treat the storefront id as an organization.
   * Org/public edit mode: prefetch the existing event organizer.
   */
  useEffect(() => {
    if (!isOpen) {
      setAllLocations([]);
      setSearchResults([]);
      setIsPreloading(false);
      setPreloadError(undefined);
      return;
    }

    if (preloadedOrganizerLocation) {
      mergeIntoAllLocations([preloadedOrganizerLocation]);
    }

    if (organizerEntityIdsKey) {
      const ids = organizerEntityIdsKey.split(",");
      let cancelled = false;
      runPreload(
        getOperatorsByIds({
          variables: { operatorEntityIds: ids },
        }).then(result => {
          if (!cancelled) {
            const locations = preferOrganization(
              mapOperatorsToLocationListData(
                (result.data?.operatorsByIds ?? []).flatMap(operator =>
                  operator ? [operator] : [],
                ),
              ),
            );
            if (preloadedOrganizerLocation) {
              mergeIntoAllLocations(locations);
            } else {
              setAllLocations(locations);
            }
          }
          return result;
        }),
      );
      return () => {
        cancelled = true;
      };
    }

    if (isOperator && defaultOperatorEntityIds.length > 0) {
      runPreload(
        fetchOperators(
          { _entityId: defaultOperatorEntityIds },
          defaultOperatorEntityIds.length,
          preloadedOrganizerLocation ? mergeIntoAllLocations : setAllLocations,
        ),
      );
      return;
    }

    if (preloadedOrganizerLocation) return;

    if (!isOperator && organizerEntityIdToPrefetch) {
      runPreload(
        fetchOperators(
          { _entityId: [organizerEntityIdToPrefetch] },
          1,
          locations => mergeIntoAllLocations(locations),
        ),
      );
    }
  }, [
    isOpen,
    isOperator,
    defaultOperatorEntityIds,
    limitHostToOperatorEntityId,
    organizerEntityIdToPrefetch,
    organizerEntityIdsKey,
    preloadedOrganizerLocation,
    fetchOperators,
    getOperatorsByIds,
    mergeIntoAllLocations,
    preferOrganization,
    runPreload,
  ]);

  const prefetchOperatorsByEntityIds = useCallback(
    (entityIds: string[]) => {
      const ids = entityIds.filter(Boolean);
      if (ids.length === 0) return;

      void fetchOperators(
        { _entityId: ids },
        ids.length,
        mergeIntoAllLocations,
      );
    },
    [fetchOperators, mergeIntoAllLocations],
  );

  const searchOperators = useCallback(
    (search: string) => {
      const trimmed = search.trim();
      if (trimmed.length < 2) {
        setSearchResults([]);
        setIsSearchingOperators(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setIsSearchingOperators(true);

      void fetchOperators(
        { search: trimmed },
        SEARCH_RESULT_LIMIT,
        locations => {
          if (requestId !== searchRequestIdRef.current) return;
          setSearchResults(locations);
          setIsSearchingOperators(false);
        },
      ).catch(() => {
        if (requestId !== searchRequestIdRef.current) return;
        setIsSearchingOperators(false);
      });
    },
    [fetchOperators],
  );

  const hasPreload =
    isOperator || !!limitHostToOperatorEntityId || organizerEntityIdsKey.length > 0;

  return {
    allLocations,
    searchResults,
    searchOperators,
    prefetchOperatorsByEntityIds,
    isSearchingOperators,
    useOperatorPreload: hasPreload,
    loading: hasPreload && isPreloading,
    preloadError: hasPreload ? preloadError : undefined,
  };
};
